import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { leaveService } from "../../../../api/LeaveService/leaveService";
import { leavePolicyService } from "../../../../api/LeaveService/leavePolicyService";
import {
  createLeavePolicy,
  getLeavePolicy,
} from "../../../../store/slices/LeaveManagement/leavePolicy";
import Layout from "../../../../common/components/Layout";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const LeaveConfigurationScreen = () => {
  const dispatch = useDispatch();
  const { status, list: policies = [] } = useSelector(
    (state) => state.employeeLeavePolicy,
  );
  const [editingId, setEditingId] = useState(null);

  const initialFormState = {
    name: "",
    code: "",
    description: "",
    leaveTypeId: "",
    type: "paid",
    isActive: true,
    annualEntitlement: "",
    maxCarryOverDays: 0,
    requiresApproval: false,
    minDaysNotice: "",
    validFrom: "",
    validUntil: "",
    priority: 0,
    resetEveryYear: false,
    criteria: {},
  };

  const [formData, setFormData] = useState(initialFormState);

  const { data: leaveTypes = [] } = useQuery({
    queryKey: ["leaveTypes"],
    queryFn: leaveService.getLeaveTypes,
  });

  useEffect(() => {
    dispatch(getLeavePolicy());
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = async () => {
    const payload = {
      ...formData,
      annualEntitlement: parseFloat(formData.annualEntitlement) || 0,
      maxCarryOverDays: parseInt(formData.maxCarryOverDays) || 0,
      minDaysNotice: formData.minDaysNotice
        ? parseInt(formData.minDaysNotice)
        : undefined,
      resetEveryYear: Boolean(formData.resetEveryYear),
    };

    try {
      if (editingId) {
        await leavePolicyService.updateLeavePolicy(editingId, payload);
        toast.success("Policy updated successfully!");
      } else {
        await dispatch(createLeavePolicy(payload)).unwrap();
        toast.success("Policy created successfully!");
      }
      setEditingId(null);
      setFormData(initialFormState);
      dispatch(getLeavePolicy());
    } catch (err) {
      toast.error(err?.message || "Failed to save policy");
    }
  };

  const handleEdit = (policy) => {
    setEditingId(policy.id);
    setFormData({ ...policy });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this policy?")) {
      try {
        await leavePolicyService.deleteLeavePolicy(id);
        toast.success("Policy deleted successfully!");
        dispatch(getLeavePolicy());
      } catch (err) {
        toast.error("Failed to delete policy");
      }
    }
  };

  return (
    <Layout>
      <ToastContainer position="top-right" autoClose={3000} />
      <style>{`
        .config-card { background: #fff; border-radius: 12px; border: 1px solid #e2e8f0; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); }
        .section-title { font-size: 0.7rem; color: #475569; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 1rem; padding-bottom: 0.5rem; border-bottom: 2px solid #f1f5f9; }
        .form-label { font-size: 0.75rem; font-weight: 600; color: #334155; margin-bottom: 0.3rem; display: block; }
        .btn-action { padding: 0.4rem 0.8rem; font-size: 0.75rem; font-weight: 600; border-radius: 6px; }
        .custom-table-header th { background-color: #1e293b !important; color: #ffffff !important; font-size: 0.7rem !important; font-weight: 700 !important; letter-spacing: 0.05em !important; text-transform: uppercase !important; }
      `}</style>

      <div className="container-fluid py-4 px-lg-5">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="fw-bold m-0 text-dark">
            {editingId
              ? `Edit ${formData.name || "Policy"} Policy`
              : "Leave Configuration"}
          </h2>
          <div>
            {editingId && (
              <button
                className="btn btn-outline-secondary me-2 btn-action"
                onClick={() => {
                  setEditingId(null);
                  setFormData(initialFormState);
                }}
              >
                Cancel
              </button>
            )}
            <button
              className="btn btn-dark px-4 btn-action"
              onClick={handleSave}
              disabled={status === "loading"}
            >
              {status === "loading"
                ? "Saving..."
                : editingId
                  ? "Update"
                  : "Save"}
            </button>
          </div>
        </div>

        <div className="config-card p-4 mb-5">
          <div className="row g-4">
            <div className="col-md-4">
              <div className="section-title">General Info</div>
              <div className="mb-3">
                <label className="form-label">Name</label>
                <input
                  name="name"
                  className="form-control"
                  onChange={handleChange}
                  value={formData.name}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Code</label>
                <input
                  name="code"
                  className="form-control"
                  onChange={handleChange}
                  value={formData.code}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Base Leave Type</label>
                <select
                  name="leaveTypeId"
                  className="form-select"
                  onChange={handleChange}
                  value={formData.leaveTypeId}
                >
                  <option value="">-- Select --</option>
                  {leaveTypes.map((lt) => (
                    <option key={lt.id} value={lt.id}>
                      {lt.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-md-4">
              <div className="section-title">Entitlement</div>
              <div className="mb-3">
                <label className="form-label">Annual Entitlement</label>
                <input
                  type="number"
                  name="annualEntitlement"
                  className="form-control"
                  onChange={handleChange}
                  value={formData.annualEntitlement}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Max Carry Over</label>
                <input
                  type="number"
                  name="maxCarryOverDays"
                  className="form-control"
                  onChange={handleChange}
                  value={formData.maxCarryOverDays}
                />
              </div>
              <div className="form-check form-switch mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="resetEveryYear"
                  checked={formData.resetEveryYear}
                  onChange={handleChange}
                />
                <label className="form-check-label small fw-bold">
                  Reset Every Year
                </label>
              </div>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="requiresApproval"
                  checked={formData.requiresApproval}
                  onChange={handleChange}
                />
                <label className="form-check-label small fw-bold">
                  Requires Approval
                </label>
              </div>
            </div>

            <div className="col-md-4">
              <div className="section-title">Validity</div>
              <div className="mb-3">
                <label className="form-label">Valid From</label>
                <input
                  type="date"
                  name="validFrom"
                  className="form-control"
                  onChange={handleChange}
                  value={formData.validFrom}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Valid Until</label>
                <input
                  type="date"
                  name="validUntil"
                  className="form-control"
                  onChange={handleChange}
                  value={formData.validUntil}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1
              className="fw-bold text-dark m-0"
              style={{ letterSpacing: "-1.5px" }}
            >
              Leave Policies
            </h1>
            <p className="text-muted small mb-0">Manage Leave Policies</p>
          </div>
        </div>

        <div
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.6)",
            backdropFilter: "blur(12px)",
            borderRadius: "16px",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            overflow: "hidden",
          }}
        >
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="custom-table-header">
                <tr>
                  <th className="py-3 ps-4 border-0">Policy Name</th>
                  <th className="py-3 border-0">Code</th>
                  <th className="py-3 border-0">
                 Leave Type</th>
                  <th className="py-3 border-0">
                 Type</th>
                  <th className="py-3 border-0">Entitlement</th>
                  <th className="py-3 border-0">Valid From</th>
                  <th className="py-3 border-0">Valid Until</th>

                  <th className="py-3 pe-4 border-0 text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {policies.map((p) => (
                  <tr key={p.id}>
                    <td className="fw-bold text-dark ps-4">{p.name}</td>
                    <td className="text-muted">{p.code}</td>
                    <td className="text-muted">{p.leaveType.name}</td>
                    <td className="text-muted">{p.type}</td>
                    <td>
                      <span
                        className="badge bg-light text-dark border border-secondary-subtle"
                        style={{ fontSize: "0.7rem" }}
                      >
                        {p.annualEntitlement} Days
                      </span>
                    </td>
                    <td className="text-muted">
                      {p.validFrom
                        ? p.validFrom
                        : "N/A"}
                    </td>
                    <td className="text-muted">
                      {p.validUntil
                        ? p.validUntil
                        : "N/A"}
                    </td>

                    <td className="text-end pe-4">
                      <button
                        className="btn btn-sm btn-outline-primary btn-action me-2"
                        onClick={() => handleEdit(p)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger btn-action"
                        onClick={() => handleDelete(p.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default LeaveConfigurationScreen;
